import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import { FAQPageJsonLd } from "@/components/JsonLd";

/**
 * Homepage FAQ block — 6 high-intent Q&A items rendered visibly in static
 * HTML (native <details>) plus FAQPage JSON-LD scoped to this subset.
 *
 * Purpose:
 *   - Strengthen homepage ranking for "Thailand Elite", "Thailand Elite Visa",
 *     "Thailand Privilege Visa", "Thailand Privilege Card" by adding keyword-
 *     rich Q&A content where the highest-PageRank URL on the site already sits.
 *   - Give AI Overviews / ChatGPT / Perplexity verbatim-quotable Q&A on the
 *     homepage (so they don't have to dig two clicks deep).
 *   - Help applicants get fast answers on the page they're most likely to land on.
 *
 * Question keys are reused from the existing `faqs.items.*` translations —
 * no duplicate copy. The full 36-question /faqs page still exists and ranks
 * independently for long-tail variations.
 */

// Selected for keyword coverage + applicant intent:
//   whatIsVisa     → "Thailand Elite Visa" definition
//   whatIsTPC      → "Thailand Privilege Card" definition
//   visaDuration   → 5/10/15/20-year keyword cluster
//   eligibility    → conversion-blocker check
//   applyFamily    → family-package query
//   howToPay       → trust / payment-after-approval signal
const HOME_FAQ_KEYS = [
  "whatIsVisa",
  "whatIsTPC",
  "visaDuration",
  "eligibility",
  "applyFamily",
  "howToPay",
] as const;

const HomeFaqSection = () => {
  const { t } = useTranslation();

  const faqItems = useMemo(
    () =>
      HOME_FAQ_KEYS.map((key) => ({
        key,
        question: t(`faqs.items.${key}.q`),
        answer: t(`faqs.items.${key}.a`),
      })),
    [t],
  );

  return (
    <>
      {/* FAQPage JSON-LD scoped to the 6 surfaced questions. Both /faqs and / can
          emit FAQPage schema for the same content if it's visibly rendered. */}
      <FAQPageJsonLd
        faqs={faqItems.map(({ question, answer }) => ({ question, answer }))}
      />

      <section id="faqs" className="py-20 lg:py-28 bg-secondary">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-accent mb-3 block">
              {t("homeFaqs.badge", "Common Questions")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
              {t("homeFaqs.title", "Thailand Elite Visa FAQs")}
            </h2>
            <p className="text-base text-muted-foreground font-body max-w-2xl mx-auto leading-relaxed">
              {t(
                "homeFaqs.intro",
                "Quick answers to the most asked questions about Thailand Elite, Thailand Privilege Visa membership and our authorized GSSA application process.",
              )}
            </p>
          </motion.div>

          <div className="space-y-2 mb-10">
            {faqItems.map(({ key, question, answer }, i) => (
              <motion.details
                key={key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group bg-background rounded-xl border shadow-card px-6 open:pb-5"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-display font-semibold text-foreground text-left py-5 text-sm sm:text-base [&::-webkit-details-marker]:hidden">
                  <span>{question}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <div className="text-muted-foreground font-body leading-relaxed text-sm whitespace-pre-line">
                  {answer}
                </div>
              </motion.details>
            ))}
          </div>

          <div className="text-center">
            <LocalizedLink
              to="/faqs"
              className="inline-flex items-center gap-2 text-sm font-body font-semibold text-accent hover:underline"
            >
              {t("homeFaqs.seeAll", "Browse all 36 questions")}
              <ArrowRight className="h-4 w-4" />
            </LocalizedLink>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomeFaqSection;
